import React, { useEffect, useRef } from 'react';
import { AlertTriangle, Lock } from 'lucide-react';
import { PricingPlans } from '../Pricing/PricingPlans';
import { CheckoutAuthPrompt } from '../Pricing/CheckoutAuthPrompt';
import { useCheckout } from '../../hooks/useCheckout';
import type { CheckoutPlanId } from '../../data/pricingPlans';
import { trackCheckoutStarted, trackPricingViewed } from '../../utils/analytics';
import { trackEvent, trackCtaClick } from '../../utils/visitorTracking';
export function PricingSection() {
  const {
    onCheckout,
    pendingPlan,
    authPromptPlan,
    dismissAuthPrompt
  } = useCheckout('pricing_section');
  // This section is mounted lazily as it scrolls into view, so mount == viewed.
  // Ref guard so Strict Mode's double-invoked effect logs one view
  const viewTracked = useRef(false);
  useEffect(() => {
    if (viewTracked.current) return;
    viewTracked.current = true;
    trackPricingViewed('homepage_pricing_section');
    void trackEvent('pricing_viewed', {
      location: 'homepage_pricing_section'
    });
    void trackEvent('premium_view', {
      location: 'homepage_pricing_section'
    });
  }, []);
  const handleCheckout = (plan: CheckoutPlanId) => {
    trackCheckoutStarted(plan, 'homepage_pricing_section');
    trackCtaClick(plan, 'homepage_pricing_section', 'checkout');
    void onCheckout(plan);
  };
  return <section className="relative bg-[#050505] py-24" id="pricing" data-id="element-2801" data-mp="fzxlwoxfdqxr">
      <div className="absolute left-0 top-0 flex h-8 w-full items-center overflow-hidden bg-[#FFD700] shadow-lg" data-id="element-2802" data-mp="3ofzgv03j2fs4">
        <div className="flex whitespace-nowrap animate-marquee" data-id="element-2803" data-mp="3kenledxz30c0">
          {Array.from({
          length: 20
        }, (_, index) => <span key={index} className="mx-4 flex items-center gap-2 text-sm font-black uppercase tracking-widest text-black" data-id="element-2804" data-mp="3tdskbldg4vop">
                <AlertTriangle size={16} fill="black" data-id="element-2805" data-mp="30pmz6xihd4oc" /> CAUTION: TOXICITY
                DETECTED
              </span>)}
        </div>
      </div>
      <style data-id="element-2806" data-mp="14k4hdmbmy0d3">{`
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-marquee { animation: marquee 20s linear infinite; }
        @media (prefers-reduced-motion: reduce) { .animate-marquee { animation: none; } }
      `}</style>

      <div className="mx-auto max-w-6xl px-6 pt-16" data-id="element-2807" data-mp="1jabnd2kghqww">
        <header className="mx-auto mb-14 max-w-2xl text-center" data-id="element-2808" data-mp="lo5zdyca9ijs">
          <h2 className="text-4xl font-black uppercase leading-none tracking-tight text-white md:text-5xl" data-id="element-2809" data-mp="lppdpjedpjix">
            Invest In Your <span className="text-[#FFD700]" data-id="element-2810" data-mp="vl0yaicgxkyl">Sanity</span>
          </h2>
          <p className="mt-5 font-mono text-lg leading-relaxed text-gray-400" data-id="element-2811" data-mp="vnwuo28o41kx">
            Choose the level of clarity that fits this conversation.
          </p>
        </header>

        <PricingPlans onCheckout={handleCheckout} pendingPlan={pendingPlan} entryLayout="column" data-id="element-2812" data-mp="270fv9m4ogctp" />

        <p className="mt-8 flex items-center justify-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-gray-500" data-id="element-2813" data-mp="3vk3h5fxyxpy3">
          <Lock size={10} aria-hidden="true" data-id="element-2814" data-mp="3rjt27ds77p73" /> Secure checkout · Cancel
          anytime
        </p>
      </div>

      <CheckoutAuthPrompt plan={authPromptPlan} onClose={dismissAuthPrompt} data-id="element-2815" data-mp="3mqgpa0wvt6u6" />
    </section>;
}